import dayjs from 'dayjs'
import { useNavigate } from 'react-router'
import { REVERIFICATION_ROOT_ROUTE } from '@mobi/account/Areas/Reverification/Utils/routes'
import { useHostContext } from '@mobi/account/Common'
import {
  lastKnownRedirectionToReverify,
  setDatestampRedirectionToReverify,
} from '@mobi/account/Utils/localStorage'
import { BetAccount } from '@mobi/api-types'
import { FeatureFlags } from '@mobi/settings/featureFlags'
import { DAYS_BEFORE_REVERIFICATION_WARNING_REMINDER } from '../Areas/Reverification/Utils'
import { useBetAccount } from './useBetAccount'
import { useHostContextFeature } from './useHostContextFeature'

/**
 * Redirects the user to the Reverification flow when it is enabled and their
 * ReverificationDueDate is due soon (or has already passed), unless they've
 * already seen the Reverification flow within the reminder period for this due date.
 */
export function useReverificationRedirection(): void {
  const isRemediationEnabled = useHostContextFeature(FeatureFlags.REMEDIATION_RE_VERIFICATION.key)
  const { channel } = useHostContext()
  const navigate = useNavigate()
  const { data: betAccount } = useBetAccount()

  if (shouldRedirectToReverification(betAccount, isRemediationEnabled)) {
    if (channel === 'desktop') {
      window.location.href = `/${REVERIFICATION_ROOT_ROUTE}`
    } else {
      navigate(REVERIFICATION_ROOT_ROUTE)
    }
  }
}

function shouldRedirectToReverification(
  betAccount: BetAccount | undefined,
  isRemediationEnabled: boolean
): boolean {
  if (!betAccount || !isRemediationEnabled) return false

  const dueDate = dayjs(betAccount.ReverificationDueDate)
  const reverificationRemainingDays = dueDate.diff(dayjs(), 'days', true)

  let lastKnownRedirectDatestamp = lastKnownRedirectionToReverify(betAccount.BetAccountNumber)

  if (reverificationRemainingDays > DAYS_BEFORE_REVERIFICATION_WARNING_REMINDER) {
    // due date greater than 14 days away, so clear any previous redirection Datestamp if it exists
    if (lastKnownRedirectDatestamp) {
      setDatestampRedirectionToReverify(betAccount.BetAccountNumber, null)
    }
    return false
  }

  if (
    lastKnownRedirectDatestamp &&
    betAccount.ReverificationDueDate !== lastKnownRedirectDatestamp
  ) {
    // due date 14 days or less away, but the due date has changed since the last redirection, so clear the previous redirection Datestamp
    setDatestampRedirectionToReverify(betAccount.BetAccountNumber, null)
    lastKnownRedirectDatestamp = null
  }

  if (!lastKnownRedirectDatestamp) {
    setDatestampRedirectionToReverify(betAccount.BetAccountNumber, betAccount.ReverificationDueDate)
  }

  return !lastKnownRedirectDatestamp
}
